import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { emit } from '@/utils/eventBus';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import {
  ArrowLeft, Volume2, Mic, MicOff, Check, X, Star, 
  Trophy, Home, Loader2, ClipboardCheck
} from 'lucide-react';

const AssessmentPage = () => {
  const { childId, subject } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const isPractice = searchParams.get('practice') === '1';
  const questionLimit = Math.max(1, parseInt(searchParams.get('limit') || '0', 10)) || null;
  
  const [child, setChild] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    fetchData();
  }, [childId, subject]);

  const fetchData = async () => {
    try {
      const childRes = await api.get(`/children/${childId}`);
      setChild(childRes.data);
      await generateAssessment();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load assessment');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const generateAssessment = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/assessments/generate?child_id=${childId}&subject=${subject}`);
      const generated = response.data || {};
      // Practice-test mode: trim the question set client-side so the flow is quick.
      if (questionLimit && Array.isArray(generated.questions)) {
        generated.questions = generated.questions.slice(0, questionLimit);
      }
      setAssessment(generated);
    } catch (error) {
      console.error('Failed to generate assessment:', error);
      // Use fallback questions
      setAssessment({
        questions: getFallbackQuestions(subject)
      });
    } finally {
      setGenerating(false);
    }
  };

  const getFallbackQuestions = (subj) => {
    const questions = {
      alphabets: [
        { question: "What letter comes after 'A'?", type: "multiple_choice", options: ["B", "C", "D", "E"], correct_answer: "B" },
        { question: "What letter starts 'Cat'?", type: "multiple_choice", options: ["A", "B", "C", "D"], correct_answer: "C" },
        { question: "What is the last letter of the alphabet?", type: "multiple_choice", options: ["X", "Y", "Z", "W"], correct_answer: "Z" },
        { question: "How many letters in 'DOG'?", type: "multiple_choice", options: ["2", "3", "4", "5"], correct_answer: "3" },
        { question: "What letter makes the 'sss' sound?", type: "multiple_choice", options: ["S", "C", "Z", "X"], correct_answer: "S" },
      ],
      counting: [
        { question: "What is 2 + 3?", type: "multiple_choice", options: ["4", "5", "6", "7"], correct_answer: "5" },
        { question: "Count: ⭐⭐⭐⭐", type: "multiple_choice", options: ["3", "4", "5", "6"], correct_answer: "4" },
        { question: "What number comes after 7?", type: "multiple_choice", options: ["6", "7", "8", "9"], correct_answer: "8" },
        { question: "How many fingers on two hands?", type: "multiple_choice", options: ["5", "8", "10", "12"], correct_answer: "10" },
        { question: "What is 5 - 2?", type: "multiple_choice", options: ["2", "3", "4", "5"], correct_answer: "3" },
      ],
      reading: [
        { question: "What rhymes with 'cat'?", type: "multiple_choice", options: ["dog", "hat", "sun", "car"], correct_answer: "hat" },
        { question: "What rhymes with 'moon'?", type: "multiple_choice", options: ["star", "spoon", "sun", "sky"], correct_answer: "spoon" },
        { question: "What word starts with 'B'?", type: "multiple_choice", options: ["Apple", "Ball", "Cat", "Dog"], correct_answer: "Ball" },
        { question: "'The ___ is red.' (apple/car/ball)", type: "multiple_choice", options: ["apple", "car", "ball", "all correct"], correct_answer: "all correct" },
        { question: "Which is a color?", type: "multiple_choice", options: ["Happy", "Blue", "Fast", "Big"], correct_answer: "Blue" },
      ],
      writing: [
        { question: "Which letter has only straight lines?", type: "multiple_choice", options: ["O", "A", "S", "C"], correct_answer: "A" },
        { question: "How many letters in 'CAT'?", type: "multiple_choice", options: ["2", "3", "4", "5"], correct_answer: "3" },
        { question: "Which is a vowel?", type: "multiple_choice", options: ["B", "C", "E", "D"], correct_answer: "E" },
        { question: "What letter looks like a circle?", type: "multiple_choice", options: ["O", "L", "T", "I"], correct_answer: "O" },
        { question: "Which word is spelled correctly?", type: "multiple_choice", options: ["CAT", "KAT", "KET", "CET"], correct_answer: "CAT" },
      ],
      math: [
        { question: "3 + 4 = ?", type: "multiple_choice", options: ["6", "7", "8", "9"], correct_answer: "7" },
        { question: "10 - 3 = ?", type: "multiple_choice", options: ["5", "6", "7", "8"], correct_answer: "7" },
        { question: "Which is bigger: 5 or 3?", type: "multiple_choice", options: ["5", "3", "Same", "Neither"], correct_answer: "5" },
        { question: "2 + 2 + 2 = ?", type: "multiple_choice", options: ["4", "5", "6", "7"], correct_answer: "6" },
        { question: "What is half of 8?", type: "multiple_choice", options: ["2", "3", "4", "5"], correct_answer: "4" },
      ],
      science: [
        { question: "What do plants need to grow?", type: "multiple_choice", options: ["Toys", "Water", "Music", "Books"], correct_answer: "Water" },
        { question: "What planet do we live on?", type: "multiple_choice", options: ["Mars", "Moon", "Earth", "Sun"], correct_answer: "Earth" },
        { question: "What animal says 'Moo'?", type: "multiple_choice", options: ["Dog", "Cat", "Cow", "Bird"], correct_answer: "Cow" },
        { question: "What gives us light during the day?", type: "multiple_choice", options: ["Moon", "Stars", "Sun", "Lamp"], correct_answer: "Sun" },
        { question: "What do fish breathe in water?", type: "multiple_choice", options: ["Air", "Oxygen", "Water", "Nothing"], correct_answer: "Oxygen" },
      ],
    };
    return questions[subj] || questions.alphabets;
  };

  const { speak } = useElevenLabsTTS({ voice: 'nova', speed: 0.9 });

  const handleAnswer = (answer) => {
    const questions = assessment?.questions || [];
    const current = questions[currentQuestion];
    const correctAnswer = current.correct_answer;
    const isCorrect = answer === correctAnswer;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    setAnswers([...answers, { 
      question: current.question, 
      answer, 
      correct_answer: correctAnswer,
      is_correct: isCorrect 
    }]);
    
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedAnswer(null);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        submitAssessment([...answers, { is_correct: isCorrect }]);
      }
    }, 1200);
  };

  const submitAssessment = async (finalAnswers) => {
    try {
      const response = await api.post('/assessments/submit', {
        child_id: childId,
        subject: subject,
        answers: finalAnswers
      });
      
      setResult(response.data);
      setShowResult(true);

      // Notify sibling widgets (Reading Progress panel, dashboard tiles) to refetch.
      emit('assessment:completed', {
        childId,
        subject,
        score: response.data.score,
        passed: response.data.passed,
      });

      // Reading Progress toast — celebrate the new Lexile snapshot.
      if (['ela', 'reading', 'phonics'].includes(subject) && response.data.passed) {
        // Backend now persists lexile_score on submit; fetch fresh child to grab it.
        try {
          const childRes = await api.get(`/children/${childId}`);
          const lex = childRes.data?.lexile_score;
          if (lex) toast.success(`Your reading grew — new Lexile ${lex}L!`);
        } catch (_e) { /* toast is best-effort */ }
      }

      if (response.data.passed) {
        setShowConfetti(true);
        speak("Congratulations! You passed the assessment!");
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      // Calculate result locally
      const correct = finalAnswers.filter(a => a.is_correct).length;
      const score = Math.round((correct / finalAnswers.length) * 100);
      setResult({
        score,
        passed: score >= 70,
        correct_count: correct,
        total_count: finalAnswers.length,
        feedback: score >= 70 ? "Great job! You're learning so much!" : "Keep practicing! You're doing great!"
      });
      setShowResult(true);
    }
  };

  if (loading || generating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-sky-100 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-sky-500 mb-4" />
        <p className="text-slate-600">
          {generating ? 'Creating your assessment...' : 'Loading...'}
        </p>
      </div>
    );
  }

  const questions = assessment?.questions || [];

  if (showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex items-center justify-center p-4">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardContent className="p-8 text-center">
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${result?.passed ? 'bg-green-100' : 'bg-orange-100'}`}>
                {result?.passed ? (
                  <Trophy className="w-12 h-12 text-yellow-500" />
                ) : (
                  <Star className="w-12 h-12 text-orange-500" />
                )}
              </div>
              
              <h2 className="font-fredoka text-3xl font-bold text-slate-800 mb-2">
                {result?.passed ? 'Assessment Passed!' : 'Keep Practicing!'}
              </h2>
              
              <p className="text-2xl font-bold text-sky-500 mb-4">
                {result?.score}%
              </p>
              
              <p className="text-slate-600 mb-2">
                {result?.correct_count} out of {result?.total_count} correct
              </p>
              
              {result?.feedback && (
                <p className="text-slate-500 mb-6 italic">
                  "{result.feedback}"
                </p>
              )}
              
              {result?.level_up && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                  <p className="font-bold text-yellow-700">🎉 Level Up!</p>
                  <p className="text-yellow-600 text-sm">You've advanced to the next level!</p>
                </div>
              )}
              
              <div className="flex flex-col gap-3">
                <Link to={`/learn/${childId}`}>
                  <Button className="w-full h-14 text-lg bg-sky-500 hover:bg-sky-600" data-testid="back-to-hub-btn">
                    <Home className="w-5 h-5 mr-2" /> Back to Learning Hub
                  </Button>
                </Link>
                {!result?.passed && (
                  <Button
                    variant="outline"
                    className="w-full h-14 text-lg"
                    onClick={() => window.location.reload()}
                    data-testid="retry-assessment-btn"
                  >
                    Try Again
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-100 to-white theme-kids">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-purple-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-purple-500" />
              <h1 className="font-fredoka font-bold text-lg text-slate-800 capitalize">
                {subject} Assessment
              </h1>
            </div>
            <div className="w-10" />
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white py-2 px-4 border-b border-purple-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">
              {currentQuestion + 1} / {questions.length}
            </span>
            <Progress value={progress} className="flex-1 h-3" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="kids-card mb-6 border-purple-200">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h2 className="font-fredoka text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                    {currentQ?.question}
                  </h2>
                  
                  {child?.accessibility_settings?.tts_enabled && (
                    <Button
                      variant="ghost"
                      onClick={() => speak(currentQ?.question)}
                      className="mb-2"
                    >
                      <Volume2 className="w-5 h-5 mr-2" /> Listen
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {currentQ?.options?.map((option, index) => {
                    const isSelected = selectedAnswer === option;
                    const isCorrect = option === currentQ.correct_answer;
                    
                    let buttonClass = 'h-20 text-xl font-bold rounded-2xl border-4 transition-all ';
                    
                    if (showFeedback) {
                      if (isCorrect) {
                        buttonClass += 'bg-green-100 border-green-400 text-green-700';
                      } else if (isSelected && !isCorrect) {
                        buttonClass += 'bg-red-100 border-red-400 text-red-700';
                      } else {
                        buttonClass += 'bg-slate-100 border-slate-200 text-slate-400';
                      }
                    } else {
                      buttonClass += 'bg-white border-purple-200 hover:border-purple-400 hover:bg-purple-50 text-slate-700';
                    }
                    
                    return (
                      <Button
                        key={index}
                        className={buttonClass}
                        onClick={() => !showFeedback && handleAnswer(option)}
                        disabled={showFeedback}
                        data-testid={`assessment-option-${index}`}
                      >
                        {showFeedback && isCorrect && <Check className="w-6 h-6 mr-2" />}
                        {showFeedback && isSelected && !isCorrect && <X className="w-6 h-6 mr-2" />}
                        {option}
                      </Button>
                    );
                  })}
                </div>

                {currentQ?.hint && (
                  <p className="text-center text-slate-500 mt-6 text-sm">
                    💡 Hint: {currentQ.hint}
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AssessmentPage;
